import { useState, useEffect, useRef } from 'react';
import { Seo, usePageSeo, assetUrl, absUrl } from '../lib/seo';
import { PageSeoContent } from '../components/common/RichContent';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { 
  Search, MapPin, ChevronRight, Shield, Users, Star, Phone, 
  Sparkles, Scissors, Activity, Award, Quote, BadgeCheck, 
  ArrowRight, ArrowLeft, BookOpen, CheckCircle, Play, Clock, Heart,
  Stethoscope, GraduationCap, Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { LogoLoader } from '../components/common/LogoLoader';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { DoctorCard } from '../components/common/DoctorCard';
import { doctorsAPI, locationsAPI, seedAPI, testimonialsAPI, blogsAPI, marketingPagesAPI } from '../lib/api';
import { getWhatsAppLink, trackWhatsAppClick } from '../lib/utils';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Counter Animation Component
const AnimatedCounter = ({ end, suffix = "", duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      let start = 0;
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);
      return () => clearInterval(timer);
    }
  }, [isInView, end, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
};

export const HomePage = () => {
  const seo = usePageSeo();
  const [doctors, setDoctors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [searchCity, setSearchCity] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await seedAPI.run();
        
        const [doctorsRes, locationsRes, testimonialsRes, blogsRes] = await Promise.all([
          doctorsAPI.getAll({ doctor_status: 'approved', limit: 12 }),
          locationsAPI.getAll(),
          testimonialsAPI.getAll({ is_featured: true, limit: 10 }),
          blogsAPI.getAll({ limit: 4 }),
        ]);
        setDoctors(doctorsRes.data);
        setLocations(locationsRes.data);
        setTestimonials(testimonialsRes.data);
        setBlogs(blogsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    if (testimonials.length > 0) {
      const interval = setInterval(() => {
        setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [testimonials.length]);

  const handleWhatsAppClick = (source, doctorId = null, doctorName = null) => {
    trackWhatsAppClick(source, doctorId, doctorName);
  };

  const categories = [
    {
      name: 'Skin',
      title: 'Skin Care',
      description: 'Acne, Pigmentation, Anti-aging & More',
      icon: Sparkles,
      href: '/category/skin',
      image: 'https://images.unsplash.com/photo-1728727242233-0924178c1fb1?w=600&q=80',
      treatments: ['Acne Treatment', 'Skin Whitening', 'Anti-aging'],
    },
    {
      name: 'Hair',
      title: 'Hair Solutions',
      description: 'Transplant, PRP, Hair Loss Treatment',
      icon: Scissors,
      href: '/category/hair',
      image: 'https://images.unsplash.com/photo-1612369399165-42589a51dfaf?w=600&q=80',
      treatments: ['Hair Transplant', 'PRP Therapy', 'Hair Loss'],
      dark: true,
    },
    {
      name: 'Weight',
      title: 'Weight Loss',
      description: 'Medical Programs & Body Contouring',
      icon: Activity,
      href: '/category/weight',
      image: 'https://images.pexels.com/photos/6975474/pexels-photo-6975474.jpeg?w=600&q=80',
      treatments: ['Weight Management', 'Body Sculpting', 'Diet Programs'],
    },
    {
      name: 'Aesthetic',
      title: 'Aesthetics',
      description: 'Botox, Fillers, Laser & Chemical Peels',
      icon: Award,
      href: '/category/aesthetic',
      image: 'https://images.unsplash.com/photo-1761718209708-9ab9ba1c7252?w=600&q=80',
      treatments: ['Botox', 'Fillers', 'Laser Treatment'],
    },
  ];

  const stats = [
    { value: 500, suffix: '+', label: 'Verified Doctors', icon: Stethoscope },
    { value: 50, suffix: 'k+', label: 'Happy Patients', icon: Heart },
    { value: 32, suffix: '+', label: 'Cities', icon: Building2 },
    { value: 98, suffix: '%', label: 'Success Rate', icon: CheckCircle },
  ];

  const trustFeatures = [
    { 
      icon: BadgeCheck, 
      title: 'Verified Specialists', 
      desc: 'Every doctor is verified with credentials check, ensuring you get qualified experts.',
      color: 'bg-blue-500'
    },
    { 
      icon: Shield, 
      title: 'Safe & Confidential', 
      desc: 'Your health data is protected. We follow strict privacy protocols.',
      color: 'bg-green-500'
    },
    { 
      icon: Star, 
      title: 'Patient Reviews', 
      desc: 'Real reviews from real patients help you make informed decisions.',
      color: 'bg-amber-500'
    },
    { 
      icon: Clock, 
      title: '24/7 Support', 
      desc: 'Our support team is always available to help you find the right doctor.',
      color: 'bg-purple-500'
    },
  ];

  const marqueeItems = [
    '★ 500+ VERIFIED DOCTORS',
    '★ ADVANCED TREATMENTS', 
    '★ INDIA • UAE • UK • USA',
    '★ TRUSTED BY 50,000+ PATIENTS',
    '★ 24/7 SUPPORT',
    '★ PREMIUM CARE',
  ];

  const featuredDoctors = doctors.filter(d => d.is_featured);
  const otherDoctors = doctors.filter(d => !d.is_featured);
  const allDoctors = [...featuredDoctors, ...otherDoctors].slice(0, 12);

  return (
    <div data-testid="home-page" className="overflow-hidden">
      <Seo override={seo} title="DrPortals | Find Verified Dermatologists, Skin & Hair Specialists" description="Find and connect with verified dermatologists for skin, hair, weight and aesthetic treatments across India and worldwide. Lead to Wellness."
        jsonLd={[{ "@context": "https://schema.org", "@type": "Organization", "name": "DrPortals", "url": absUrl('/'), "logo": absUrl('/brand/logo-dark.png'), "contactPoint": { "@type": "ContactPoint", "telephone": "+91-88285-09080", "contactType": "customer service" } },
          { "@context": "https://schema.org", "@type": "WebSite", "name": "DrPortals", "url": absUrl('/') }]} />
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-[95vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background Video/Image with Parallax */}
        <motion.div 
          style={{ scale: heroScale }}
          className="absolute inset-0 z-0"
        >
          <img 
            src="https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=1920&q=80"
            alt="Premium dermatology clinic"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#23265d]/95 via-[#23265d]/85 to-[#23265d]/70" />
        </motion.div>

        {/* Floating Decorative Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div 
            animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 right-[15%] w-40 h-40 bg-[#c5a065]/20 rounded-full blur-3xl"
          />
          <motion.div 
            animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute bottom-40 left-[10%] w-56 h-56 bg-[#5c65ff]/10 rounded-full blur-3xl"
          />
        </div>

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-24 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="flex items-center gap-3 mb-6"
              >
                <span className="px-4 py-1.5 bg-[#c5a065]/20 backdrop-blur-sm rounded-full text-sm font-medium text-[#c5a065] tracking-wide border border-[#c5a065]/30">
                  #1 Dermatology Platform
                </span>
                <span className="flex items-center gap-1 text-white/70 text-sm">
                  <Star className="w-4 h-4 text-amber-400 fill-current" />
                  4.9 Rating
                </span>
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="font-accent text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.1] mb-6 text-white tracking-tight"
              >
                {seo.h1 || (<>Find India's Best
                <span className="block text-[#c5a065] italic mt-2">Dermatologists</span></>)}
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-lg md:text-xl text-white/70 mb-8 max-w-xl leading-relaxed"
              >
                Connect with verified skin specialists across 32+ cities. Expert care for skin, hair, weight, and aesthetic treatments.
              </motion.p>

              {/* Glassmorphism Search Box */}
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="glass rounded-2xl p-4 md:p-5 shadow-2xl max-w-xl"
                data-testid="hero-search-box"
              >
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <Select value={searchCity} onValueChange={setSearchCity}>
                      <SelectTrigger className="h-14 bg-white/50 border-0 rounded-xl text-gray-800 text-base focus:ring-2 focus:ring-[#c5a065]" data-testid="city-select">
                        <div className="flex items-center gap-3">
                          <MapPin className="w-5 h-5 text-[#c5a065]" />
                          <SelectValue placeholder="Select Your City" />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc.id} value={loc.slug}>
                            {loc.city}, {loc.country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Link to={searchCity ? `/city/${searchCity}` : '/doctors'}>
                    <Button 
                      className="h-14 px-8 rounded-xl bg-[#23265d] hover:bg-[#1e1e4b] text-white w-full sm:w-auto text-base font-medium transition-all hover:scale-[1.02] hover:shadow-lg"
                      data-testid="hero-search-button"
                    >
                      <Search className="w-5 h-5 mr-2" />
                      Find Doctors
                    </Button>
                  </Link>
                </div>
              </motion.div>

              {/* Quick Stats */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="flex flex-wrap items-center gap-6 mt-8"
              >
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2">
                    {[1,2,3,4].map(i => (
                      <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-[#c5a065] to-amber-400 border-2 border-[#23265d] flex items-center justify-center text-white text-xs font-semibold">
                        {['Dr', 'MD', 'MS', 'MB'][i-1]}
                      </div>
                    ))}
                  </div>
                  <span className="text-white/70 text-sm">500+ Verified Doctors</span>
                </div>
                <div className="h-6 w-px bg-white/20" />
                <span className="flex items-center gap-2 text-sm text-white/70">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  Instant WhatsApp Support
                </span>
              </motion.div>
            </div>

            {/* Right Side - Featured Doctor Cards Preview */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="hidden lg:block"
            >
              <div className="relative">
                {/* Floating Cards Effect */}
                <motion.div
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-4 -right-4 bg-white rounded-2xl p-4 shadow-xl w-48"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">This Month</p>
                      <p className="font-bold text-gray-900">2,340+</p>
                      <p className="text-xs text-gray-500">Consultations</p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  animate={{ y: [0, 10, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-xl w-52 z-10"
                >
                  <div className="flex items-center gap-1 mb-2">
                    {[1,2,3,4,5].map(i => (
                      <Star key={i} className="w-4 h-4 text-amber-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 italic">"Best dermatologist! Highly recommended."</p>
                  <p className="text-xs text-gray-400 mt-2">- Priya M., Mumbai</p>
                </motion.div>

                {/* Main Image */}
                <div className="bg-gradient-to-br from-[#c5a065]/20 to-[#23265d]/20 rounded-3xl p-3">
                  <img 
                    src="https://images.pexels.com/photos/5327585/pexels-photo-5327585.jpeg?w=500&q=80"
                    alt="Doctor consultation"
                    className="rounded-2xl w-full h-[400px] object-cover"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div 
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Marquee Strip */}
      <div className="bg-[#23265d] py-4 border-y border-[#c5a065]/20">
        <Marquee speed={40} gradient={false} className="overflow-hidden">
          {marqueeItems.map((item, i) => (
            <span key={i} className="mx-8 text-sm font-medium tracking-[0.2em] text-[#c5a065]/80">
              {item}
            </span>
          ))}
        </Marquee>
      </div>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-gray-100" data-testid="quick-stats">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <motion.div 
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <div className="w-12 h-12 bg-[#23265d]/5 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-6 h-6 text-[#c5a065]" />
                  </div>
                  <div className="font-accent text-3xl md:text-4xl font-semibold text-[#23265d]">
                    {typeof stat.value === 'number' && stat.value > 10 ? (
                      <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                    ) : (
                      <>{stat.value}{stat.suffix}</>
                    )}
                  </div>
                  <div className="text-gray-500 text-sm mt-1">{stat.label}</div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trust Features Section */}
      <AnimatedSection className="py-16 md:py-24 bg-[#FDFCF8]" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-[#c5a065] text-sm font-semibold tracking-[0.2em] uppercase">
              Why DrPortals
            </span>
            <h2 className="font-accent text-3xl md:text-4xl font-semibold text-[#23265d] mt-4 tracking-tight">
              Trusted by 50,000+ Patients
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trustFeatures.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div 
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="bg-white rounded-2xl p-6 shadow-soft hover:shadow-hover transition-all"
                >
                  <div className={`w-12 h-12 ${feature.color} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-[#23265d] mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </AnimatedSection>

      {/* Doctors Carousel Section */}
      <AnimatedSection className="py-16 md:py-24 px-4 md:px-8 bg-white" data-testid="doctors-carousel-section">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-10">
            <div>
              <span className="text-[#c5a065] text-sm font-semibold tracking-[0.2em] uppercase">
                Our Specialists
              </span>
              <h2 className="font-accent text-3xl md:text-4xl font-semibold text-[#23265d] mt-4 tracking-tight">
                Top Dermatologists
              </h2>
              <p className="text-gray-500 mt-2 max-w-md">
                Handpicked experts with proven track records in dermatology
              </p>
            </div>
            <Link to="/doctors">
              <Button variant="outline" className="rounded-full border-[#23265d] text-[#23265d] hover:bg-[#23265d] hover:text-white px-6 group">
                View All Doctors
                <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <LogoLoader />
          ) : (
            <Carousel
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent className="-ml-4">
                {allDoctors.map((doctor, index) => (
                  <CarouselItem key={doctor.id} className="pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      viewport={{ once: true }}
                    >
                      <DoctorCard doctor={doctor} featured={index === 0} />
                    </motion.div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <div className="flex justify-center gap-2 mt-8">
                <CarouselPrevious className="static translate-y-0 bg-[#23265d] text-white hover:bg-[#1e1e4b] hover:text-white border-0" />
                <CarouselNext className="static translate-y-0 bg-[#23265d] text-white hover:bg-[#1e1e4b] hover:text-white border-0" />
              </div>
            </Carousel>
          )}
        </div>
      </AnimatedSection>

      {/* Categories Section */}
      <AnimatedSection className="py-16 md:py-24 px-4 md:px-8 bg-[#FDFCF8]" data-testid="categories-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#c5a065] text-sm font-semibold tracking-[0.2em] uppercase">
              Treatments
            </span>
            <h2 className="font-accent text-3xl md:text-4xl font-semibold text-[#23265d] mt-4 tracking-tight">
              Explore Our Specialities
            </h2>
          </div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {categories.map((category) => {
              const Icon = category.icon;
              return (
                <motion.div key={category.name} variants={scaleIn}>
                  <Link
                    to={category.href}
                    className="group relative block h-[380px] rounded-2xl overflow-hidden"
                    data-testid={`category-${category.name.toLowerCase()}`}
                  >
                    <div className="absolute inset-0">
                      <img 
                        src={category.image} 
                        alt={category.title} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                      />
                      <div className={`absolute inset-0 bg-gradient-to-t ${category.dark ? 'from-gray-900/90 via-gray-900/60' : 'from-[#23265d]/90 via-[#23265d]/40'} to-transparent`} />
                    </div>
                    <div className="relative h-full flex flex-col justify-end p-6">
                      <div className="w-14 h-14 bg-white/90 backdrop-blur rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Icon className="w-7 h-7 text-[#23265d]" />
                      </div>
                      <h3 className="font-accent text-2xl font-semibold text-white mb-2">
                        {category.title}
                      </h3>
                      <p className="text-white/80 text-sm mb-4">
                        {category.description}
                      </p>
                      {/* Treatment Tags */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {category.treatments.map((treatment, i) => (
                          <span key={i} className="px-2 py-1 bg-white/20 backdrop-blur rounded-full text-xs text-white">
                            {treatment}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 text-[#c5a065] font-medium text-sm opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                        Find Specialists <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Testimonials Section */}
      <AnimatedSection className="py-16 md:py-24 px-4 md:px-8 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#c5a065] text-sm font-semibold tracking-[0.2em] uppercase">
              Success Stories
            </span>
            <h2 className="font-accent text-3xl md:text-4xl font-semibold text-[#23265d] mt-4 tracking-tight">
              What Our Patients Say
            </h2>
          </div>

          {testimonials.length > 0 && (
            <div className="max-w-4xl mx-auto">
              {/* Main Testimonial */}
              <motion.div 
                key={activeTestimonial}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-gradient-to-br from-[#23265d] to-[#1e1e4b] rounded-3xl p-8 md:p-12 text-center relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#c5a065]/10 rounded-full blur-3xl" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#5c65ff]/10 rounded-full blur-3xl" />
                <div className="relative z-10">
                  <Quote className="w-12 h-12 text-[#c5a065]/30 mx-auto mb-6" />
                  <div className="flex justify-center gap-1 mb-6">
                    {[...Array(testimonials[activeTestimonial]?.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-[#c5a065] fill-current" />
                    ))}
                  </div>
                  <p className="font-accent text-xl md:text-2xl text-white/90 leading-relaxed mb-8 italic">
                    "{testimonials[activeTestimonial]?.review}"
                  </p>
                  <div>
                    <p className="font-semibold text-white text-lg">{testimonials[activeTestimonial]?.name}</p>
                    <p className="text-white/60 text-sm">{testimonials[activeTestimonial]?.location} • {testimonials[activeTestimonial]?.treatment}</p>
                  </div>
                </div>
              </motion.div>

              {/* Testimonial Dots */}
              <div className="flex justify-center gap-2 mt-8">
                {testimonials.slice(0, 8).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveTestimonial(i)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      i === activeTestimonial ? 'bg-[#c5a065] w-8' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    data-testid={`testimonial-dot-${i}`}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </AnimatedSection>

      {/* Treatment Gallery */}
      <AnimatedSection className="py-16 md:py-24 px-4 md:px-8 bg-[#FDFCF8]" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#c5a065] text-sm font-semibold tracking-[0.2em] uppercase">
              Our Facilities
            </span>
            <h2 className="font-accent text-3xl md:text-4xl font-semibold text-[#23265d] mt-4 tracking-tight">
              World-Class Treatment Centers
            </h2>
          </div>
          
          <div className="grid grid-cols-12 gap-4 md:gap-6">
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="col-span-12 md:col-span-8 rounded-2xl overflow-hidden h-[300px] md:h-[400px]"
            >
              <img src="https://images.unsplash.com/photo-1713085085470-fba013d67e65?auto=format&fit=crop&w=1200&q=75" alt="Dermatologist performing a facial skin treatment in a modern clinic" loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
            <div className="col-span-12 md:col-span-4 grid gap-4 md:gap-6">
              <motion.div whileHover={{ scale: 1.02 }} className="rounded-2xl overflow-hidden h-[190px]">
                <img src="https://images.pexels.com/photos/6011602/pexels-photo-6011602.jpeg?w=400" alt="Skin treatment" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </motion.div>
              <motion.div whileHover={{ scale: 1.02 }} className="rounded-2xl overflow-hidden h-[190px]">
                <img src="https://images.pexels.com/photos/5069432/pexels-photo-5069432.jpeg?w=400" alt="Consultation" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
              </motion.div>
            </div>
            <motion.div whileHover={{ scale: 1.02 }} className="col-span-6 md:col-span-4 rounded-2xl overflow-hidden h-48">
              <img src="https://images.pexels.com/photos/3985333/pexels-photo-3985333.jpeg?w=400" alt="Aesthetic" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
            <motion.div whileHover={{ scale: 1.02 }} className="col-span-6 md:col-span-4 rounded-2xl overflow-hidden h-48">
              <img src="https://images.pexels.com/photos/3993449/pexels-photo-3993449.jpeg?w=400" alt="Hair treatment" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
            <motion.div whileHover={{ scale: 1.02 }} className="col-span-12 md:col-span-4 rounded-2xl overflow-hidden h-48">
              <img src="https://images.pexels.com/photos/5327656/pexels-photo-5327656.jpeg?w=400" alt="Equipment" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      {/* Blog Section */}
      {blogs.length > 0 && (
        <AnimatedSection className="py-16 md:py-24 px-4 md:px-8 bg-white" data-testid="blog-section">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-10">
              <div>
                <span className="text-[#c5a065] text-sm font-semibold tracking-[0.2em] uppercase">
                  Knowledge Hub
                </span>
                <h2 className="font-accent text-3xl md:text-4xl font-semibold text-[#23265d] mt-4 tracking-tight">
                  Expert Health Insights
                </h2>
              </div>
              <Link to="/blogs">
                <Button variant="outline" className="rounded-full border-[#23265d] text-[#23265d] hover:bg-[#23265d] hover:text-white px-6 group">
                  View All Articles
                  <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {blogs.slice(0, 4).map((blog, i) => (
                <motion.div 
                  key={blog.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link to={`/blog/${blog.slug}`} className="group block">
                    <div className="rounded-2xl overflow-hidden bg-[#FDFCF8] hover:shadow-hover transition-shadow duration-300">
                      <div className="h-48 overflow-hidden">
                        <img 
                          src={blog.featured_image || 'https://images.unsplash.com/photo-1655026392641-bf283a5f12d4?auto=format&fit=crop&w=1200&q=75'} 
                          alt={blog.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <span className="text-xs text-[#c5a065] font-semibold uppercase tracking-wider">{blog.category}</span>
                        <h3 className="font-semibold text-[#23265d] mt-2 line-clamp-2 group-hover:text-[#c5a065] transition-colors">
                          {blog.title}
                        </h3>
                        <div className="flex items-center gap-2 mt-4 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          {blog.read_time || 5} min read
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </AnimatedSection>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-28 px-4 md:px-8 bg-gradient-to-br from-[#23265d] via-[#1e1e4b] to-[#23265d] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#c5a065]/10 rounded-full blur-[150px]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-accent text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6 tracking-tight">
              Ready to Find Your
              <span className="block text-[#c5a065] italic mt-2">Perfect Specialist?</span>
            </h2>
            <p className="text-lg text-white/70 mb-10 max-w-xl mx-auto leading-relaxed">
              Our team will connect you with the right dermatologist based on your specific needs and location.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a 
                href={getWhatsAppLink("Hi, I need help finding a dermatologist.")} 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => handleWhatsAppClick('homepage-cta')}
              >
                <Button className="rounded-full bg-[#25D366] hover:bg-[#25D366]/90 text-white px-10 py-7 text-lg gap-3 shadow-[0_0_30px_rgba(37,211,102,0.4)] hover:shadow-[0_0_40px_rgba(37,211,102,0.6)] transition-all" data-testid="cta-whatsapp">
                  <Phone className="w-5 h-5" />
                  Chat on WhatsApp
                </Button>
              </a>
              <Link to="/register">
                <Button variant="outline" className="rounded-full border-white/30 text-white hover:bg-white hover:text-[#23265d] px-10 py-7 text-lg">
                  Join as Doctor
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Popular Cities */}
      <AnimatedSection className="py-16 md:py-24 px-4 md:px-8 bg-[#FDFCF8]" data-testid="popular-cities-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-[#c5a065] text-sm font-semibold tracking-[0.2em] uppercase">
              Our Network
            </span>
            <h2 className="font-accent text-3xl md:text-4xl font-semibold text-[#23265d] mt-4 tracking-tight">
              Find Doctors in Your City
            </h2>
          </div>
          
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4"
          >
            {locations.slice(0, 12).map((location) => (
              <motion.div key={location.id} variants={fadeInUp}>
                <Link
                  to={`/city/${location.slug}`}
                  className="group flex flex-col items-center p-4 bg-white rounded-xl border border-gray-100 hover:border-[#c5a065]/30 hover:shadow-hover transition-all text-center"
                >
                  <div className="w-10 h-10 bg-[#23265d]/5 rounded-full flex items-center justify-center mb-2 group-hover:bg-[#23265d] group-hover:scale-110 transition-all duration-300">
                    <MapPin className="w-5 h-5 text-[#c5a065] group-hover:text-white transition-colors" />
                  </div>
                  <span className="font-medium text-[#23265d] text-sm">{location.city}</span>
                  <span className="text-xs text-gray-400">{location.country}</span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </AnimatedSection>
      <PageSeoContent seo={seo} />
    </div>
  );
};
